import React, { useState } from "react";
import SEOHead from "@/components/SEOHead";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { generatePredictions, getVisibleCount, isGameTypeAvailable, type GameType, type SubscriptionPlan } from "@/utils/predictionUtils";
import AISelector from "@/components/prediction/AISelector";
import StateSelector from "@/components/prediction/StateSelector";
import LotterySelector from "@/components/prediction/LotterySelector";
import SharePredictionDialog from "@/components/SharePredictionDialog";
import GerarJogosHeader from "@/components/pages/gerar-jogos/GerarJogosHeader";
import PredictionContent from "@/components/pages/gerar-jogos/PredictionContent";
import UpgradeBanner from "@/components/pages/gerar-jogos/UpgradeBanner";
import GameCountSelector from "@/components/pages/gerar-jogos/GameCountSelector";
import BicharGroupSelector from "@/components/prediction/BicharGroupSelector";
import TrialStatusBanner from "@/components/TrialStatusBanner";
import ActiveTrialBanner from "@/components/ActiveTrialBanner";
import { Crown, Target, Layers, Users } from "lucide-react";
import PageInstructions from "@/components/PageInstructions";
import DisclaimerEstatistico from "@/components/legal/DisclaimerEstatistico";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAnalytics } from "@/hooks/useAnalytics";

const GerarJogosPage = () => {
  const { user, getTrialInfo } = useAuth();
  const { track } = useAnalytics();
  useEffect(() => { track("tool_access", { tool: "gerar-jogos" }); }, [track]);
  const [selectedAI, setSelectedAI] = useState<"gemini" | "chatgpt" | "claude" | "deepseek">("gemini");
  const [selectedState, setSelectedState] = useState<string>("");
  const [selectedLottery, setSelectedLottery] = useState<string>("jogo-bicho");
  const [gameCount, setGameCount] = useState<number>(5);
  const [selectedBicharGroups, setSelectedBicharGroups] = useState<string[]>([]);
  const [showLastUpdate, setShowLastUpdate] = useState(true);
  const [userPlan, setUserPlan] = useState<SubscriptionPlan>("free");
  const [predictions, setPredictions] = useState<{
    milhar: string[];
    centena: string[];
    grupo: string[];
    duque: string[];
    terno: string[];
  }>({
    milhar: [],
    centena: [],
    grupo: [],
    duque: [],
    terno: []
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [shareDialog, setShareDialog] = useState<{
    open: boolean;
    predictions: string[];
    type: GameType;
    title: string;
  }>({
    open: false,
    predictions: [],
    type: "milhar",
    title: ""
  });

  // Função para limpar os palpites
  const clearPredictions = () => {
    setPredictions({
      milhar: [],
      centena: [],
      grupo: [],
      duque: [],
      terno: []
    });
  };

  // Handlers que limpam os palpites ao alterar configurações
  const handleAIChange = (value: "gemini" | "chatgpt" | "claude" | "deepseek") => {
    setSelectedAI(value);
    clearPredictions();
  };

  const handleStateChange = (value: string) => {
    setSelectedState(value);
    clearPredictions();
  };

  const handleLotteryChange = (value: string) => {
    setSelectedLottery(value);
    clearPredictions();
  };

  const handleGameCountChange = (value: number) => {
    setGameCount(value);
    clearPredictions();
  };

  // Verificar plano do usuário
  useEffect(() => {
    const checkUserPlan = async () => {
      if (!user?.id) return;

      try {
        const { data: profile } = await supabase
          .from("profiles")
          .select("plan, created_at, is_subscribed")
          .eq("id", user.id)
          .single();

        if (profile) {
          setUserPlan((profile.plan as SubscriptionPlan) || "free");
        }
      } catch (error) {
        console.error("Erro ao verificar plano do usuário:", error);
      }
    };

    checkUserPlan();
  }, [user]);

  const trialInfo = getTrialInfo();
  const isFederalLottery = selectedLottery === "federal" || selectedLottery === "federal-extra";
  const isAccessExpired = trialInfo.status === 'trial_expired' || trialInfo.status === 'subscription_expired';

  // CRÍTICO: Apenas usuários com acesso válido têm plano premium efetivo
  const effectiveUserPlan: SubscriptionPlan = (!isAccessExpired && trialInfo.isActive) || user?.isSubscribed ? "premium" : "free";

  const navigate = useNavigate();
  useEffect(() => {
    if (user && userPlan === "free" && isAccessExpired) {
      // Redireciona para planos quando o trial expirar e o usuário ainda for free
      navigate("/planos");
    }
  }, [user, userPlan, isAccessExpired, navigate]);

  const generatePredictionsForType = async (gameType: GameType) => {
    if (!selectedState) {
      alert("Por favor, selecione um estado primeiro.");
      return;
    }

    // Bloqueia apenas quando trial expirou e usuário continua free
    if (effectiveUserPlan === "free" && isAccessExpired) {
      alert("Seu período de teste de 2 dias expirou. Faça upgrade para continuar gerando palpites.");
      return;
    }

    setIsGenerating(true);
    
    try {
      if (isGameTypeAvailable(effectiveUserPlan, gameType)) {
        // Para free (após expirar o trial), gera mais porém exibe limitado; durante trial = premium
        const totalCount = effectiveUserPlan === "free" 
          ? Math.max(gameCount, 10) 
          : gameCount;
        const generated = generatePredictions(gameType, totalCount, selectedAI, selectedBicharGroups);
        setPredictions(prev => ({
          ...prev,
          [gameType]: generated.map(p => p.value)
        }));
      }
    } catch (error) {
      console.error("Erro ao gerar palpites:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleShare = (gameType: GameType, title: string) => {
    const preds = predictions[gameType] || [];
    if (preds.length === 0) {
      return;
    }

    // CRÍTICO: Para free (após expirar acesso) compartilha apenas visíveis; para premium = completo
    const visiblePreds = effectiveUserPlan === "free" ? preds.slice(0, 2) : preds.slice(0, gameCount);
    
    setShareDialog({
      open: true,
      predictions: visiblePreds,
      type: gameType,
      title: title
    });
  };

  // Função para obter o nome da loteria
  const getLotteryName = (lotteryValue: string) => {
    const lotteryNames: { [key: string]: string } = {
      "paratodos": "Paratodos",
      "look-goias": "Look Goiás",
      "jogo-bicho": "Jogo do Bicho",
      "federal": "Federal",
      "federal-extra": "Federal Extra",
      "maluquinha": "Maluquinha",
      "minas-gerais": "Minas Gerais",
      "bahia": "Bahia",
      "bahia-maluca": "Bahia Maluca",
      "capital": "Capital",
      "monte-carlos": "Monte Carlos",
      "lotece-lotep": "LOTECE/LOTEP",
      "nacional": "Nacional",
      "sao-paulo-pt": "São Paulo PT"
    };
    return lotteryNames[lotteryValue] || lotteryValue;
  };

  // Background dinâmico baseado na loteria
  const pageBackground = isFederalLottery
    ? "min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-yellow-900/20"
    : "min-h-screen bg-gradient-to-br from-background via-background to-primary/5";

  return (
    <>
      <SEOHead
        title="Gerar Palpites Jogo do Bicho com IA - Milhares, Centenas, Dezenas e Grupos"
        description="Gere palpites inteligentes para o Jogo do Bicho com Inteligência Artificial. Milhares, centenas, dezenas, grupos, duques e ternos. IA analisa padrões e tendências para maximizar suas chances."
        keywords="gerar palpites jogo do bicho, palpites bicho hoje, bicho que vai dar hoje, previsão jogo do bicho, milhar bicho, centena bicho, dezena bicho, grupo bicho, palpites com IA bicho"
        canonical="/gerar-jogos"
      />
    <div className={pageBackground}>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <GerarJogosHeader isFederalLottery={isFederalLottery} />

          <PageInstructions
            title="📖 Como usar o Gerador de Jogos"
            steps={[
              "Escolha a Inteligência Artificial que vai gerar seus palpites (Gemini, ChatGPT, Claude ou DeepSeek)",
              "Selecione o estado da loteria (ex: Rio de Janeiro, São Paulo)",
              "Escolha qual loteria você quer jogar (Jogo do Bicho, Federal, etc.)",
              "Defina quantos jogos deseja gerar",
              "Clique no botão 'Gerar Palpites' e aguarde a IA processar",
              "Veja os resultados abaixo — você pode compartilhar ou salvar os palpites gerados"
            ]}
          />

          {/* Trial Status Banner */}
          <TrialStatusBanner />
          
          {/* Active Trial Banner */}
          <ActiveTrialBanner />

          {/* AI, State, Lottery, Game Count and Bichar Group Selectors */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-8 flex-wrap">
            <AISelector 
              selectedAI={selectedAI} 
              onAIChange={handleAIChange}
              disabled={userPlan === "free" && isAccessExpired}
            />
            <StateSelector 
              selectedState={selectedState} 
              onStateChange={handleStateChange}
              disabled={userPlan === "free" && isAccessExpired}
            />
            <LotterySelector 
              selectedLottery={selectedLottery} 
              onLotteryChange={handleLotteryChange}
              disabled={userPlan === "free" && isAccessExpired}
            />
            <GameCountSelector 
              selectedCount={gameCount} 
              onCountChange={handleGameCountChange}
              isFederalLottery={isFederalLottery}
            />
            {selectedLottery === "jogo-bicho" && (
              <BicharGroupSelector
                selectedGroups={selectedBicharGroups}
                onGroupsChange={setSelectedBicharGroups}
                disabled={userPlan === "free" && isAccessExpired}
              />
            )}
          </div>

          {/* Tabs Navigation - Horizontal */}
          <Tabs defaultValue="milhar" className="w-full">
            <TabsList className={`grid w-full grid-cols-5 mb-8 h-auto p-1 ${
              isFederalLottery 
                ? 'bg-gray-800/80 border-yellow-400/50' 
                : ''
            }`}>
              <TabsTrigger 
                value="milhar" 
                className={`flex flex-col items-center gap-2 p-4 ${
                  isFederalLottery 
                    ? 'data-[state=active]:bg-yellow-600 data-[state=active]:text-black text-yellow-200 hover:bg-yellow-700/50' 
                    : ''
                }`}
                disabled={userPlan === "free" && isAccessExpired}
              >
                <Target className="h-5 w-5" />
                <span className="text-sm font-medium">Milhar</span>
              </TabsTrigger>
              <TabsTrigger 
                value="centena" 
                className={`flex flex-col items-center gap-2 p-4 ${
                  isFederalLottery 
                    ? 'data-[state=active]:bg-yellow-600 data-[state=active]:text-black text-yellow-200 hover:bg-yellow-700/50' 
                    : ''
                }`}
                disabled={userPlan === "free" && isAccessExpired}
              >
                <Layers className="h-5 w-5" />
                <span className="text-sm font-medium">Centena</span>
              </TabsTrigger>
              <TabsTrigger 
                value="grupo" 
                className={`flex flex-col items-center gap-2 p-4 ${
                  isFederalLottery 
                    ? 'data-[state=active]:bg-yellow-600 data-[state=active]:text-black text-yellow-200 hover:bg-yellow-700/50' 
                    : ''
                }`}
                disabled={!isGameTypeAvailable(userPlan, "grupo") || (userPlan === "free" && isAccessExpired)}
              >
                <Users className="h-5 w-5" />
                <span className="text-sm font-medium">Grupo</span>
                {!isGameTypeAvailable(userPlan, "grupo") && (
                  <Crown className="h-3 w-3 text-yellow-500" />
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="duque" 
                className={`flex flex-col items-center gap-2 p-4 ${
                  isFederalLottery 
                    ? 'data-[state=active]:bg-yellow-600 data-[state=active]:text-black text-yellow-200 hover:bg-yellow-700/50' 
                    : ''
                }`}
                disabled={!isGameTypeAvailable(userPlan, "duque") || (userPlan === "free" && isAccessExpired)}
              >
                <span className="text-lg font-bold">D</span>
                <span className="text-sm font-medium">Duque</span>
                {!isGameTypeAvailable(userPlan, "duque") && (
                  <Crown className="h-3 w-3 text-yellow-500" />
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="terno" 
                className={`flex flex-col items-center gap-2 p-4 ${
                  isFederalLottery 
                    ? 'data-[state=active]:bg-yellow-600 data-[state=active]:text-black text-yellow-200 hover:bg-yellow-700/50' 
                    : ''
                }`}
                disabled={!isGameTypeAvailable(userPlan, "terno") || (userPlan === "free" && isAccessExpired)}
              >
                <span className="text-lg font-bold">T</span>
                <span className="text-sm font-medium">Terno</span>
                {!isGameTypeAvailable(userPlan, "terno") && (
                  <Crown className="h-3 w-3 text-yellow-500" />
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="milhar">
              <Card className={isFederalLottery ? 'bg-gray-800/60 border-yellow-400/50 backdrop-blur-sm' : ''}>
                <CardContent className="p-6">
                  <PredictionContent
                    gameType="milhar"
                    title="Milhar"
                    predictions={predictions.milhar}
                    userPlan={effectiveUserPlan}
                    isFederalLottery={isFederalLottery}
                    isGenerating={isGenerating}
                    selectedState={selectedState}
                    selectedAI={selectedAI}
                    selectedLottery={selectedLottery}
                    gameCount={gameCount}
                    showLastUpdate={showLastUpdate}
                    setShowLastUpdate={setShowLastUpdate}
                    onGeneratePredictions={generatePredictionsForType}
                    onShare={handleShare}
                    getLotteryName={getLotteryName}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="centena">
              <Card className={isFederalLottery ? 'bg-gray-800/60 border-yellow-400/50 backdrop-blur-sm' : ''}>
                <CardContent className="p-6">
                  <PredictionContent
                    gameType="centena"
                    title="Centena"
                    predictions={predictions.centena}
                    userPlan={effectiveUserPlan}
                    isFederalLottery={isFederalLottery}
                    isGenerating={isGenerating}
                    selectedState={selectedState}
                    selectedAI={selectedAI}
                    selectedLottery={selectedLottery}
                    gameCount={gameCount}
                    showLastUpdate={showLastUpdate}
                    setShowLastUpdate={setShowLastUpdate}
                    onGeneratePredictions={generatePredictionsForType}
                    onShare={handleShare}
                    getLotteryName={getLotteryName}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="grupo">
              <Card className={isFederalLottery ? 'bg-gray-800/60 border-yellow-400/50 backdrop-blur-sm' : ''}>
                <CardContent className="p-6">
                  <PredictionContent
                    gameType="grupo"
                    title="Grupo"
                    predictions={predictions.grupo}
                    userPlan={effectiveUserPlan}
                    isFederalLottery={isFederalLottery}
                    isGenerating={isGenerating}
                    selectedState={selectedState}
                    selectedAI={selectedAI}
                    selectedLottery={selectedLottery}
                    gameCount={gameCount}
                    showLastUpdate={showLastUpdate}
                    setShowLastUpdate={setShowLastUpdate}
                    onGeneratePredictions={generatePredictionsForType}
                    onShare={handleShare}
                    getLotteryName={getLotteryName}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="duque">
              <Card className={isFederalLottery ? 'bg-gray-800/60 border-yellow-400/50 backdrop-blur-sm' : ''}>
                <CardContent className="p-6">
                  <PredictionContent
                    gameType="duque"
                    title="Duque de Dezenas"
                    predictions={predictions.duque}
                    userPlan={effectiveUserPlan}
                    isFederalLottery={isFederalLottery}
                    isGenerating={isGenerating}
                    selectedState={selectedState}
                    selectedAI={selectedAI}
                    selectedLottery={selectedLottery}
                    gameCount={gameCount}
                    showLastUpdate={showLastUpdate}
                    setShowLastUpdate={setShowLastUpdate}
                    onGeneratePredictions={generatePredictionsForType}
                    onShare={handleShare}
                    getLotteryName={getLotteryName}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="terno">
              <Card className={isFederalLottery ? 'bg-gray-800/60 border-yellow-400/50 backdrop-blur-sm' : ''}>
                <CardContent className="p-6">
                  <PredictionContent
                    gameType="terno"
                    title="Terno de Dezenas"
                    predictions={predictions.terno}
                    userPlan={effectiveUserPlan}
                    isFederalLottery={isFederalLottery}
                    isGenerating={isGenerating}
                    selectedState={selectedState}
                    selectedAI={selectedAI}
                    selectedLottery={selectedLottery}
                    gameCount={gameCount}
                    showLastUpdate={showLastUpdate}
                    setShowLastUpdate={setShowLastUpdate}
                    onGeneratePredictions={generatePredictionsForType}
                    onShare={handleShare}
                    getLotteryName={getLotteryName}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Upgrade Banner for Free Users */}
          <UpgradeBanner userPlan={userPlan} isFederalLottery={isFederalLottery} />

          <DisclaimerEstatistico />
        </div>
      </div>

      {/* Share Dialog */}
      <SharePredictionDialog
        open={shareDialog.open}
        onClose={() => setShareDialog(prev => ({ ...prev, open: false }))}
        predictions={shareDialog.predictions}
        predictionType={shareDialog.type}
        title={shareDialog.title}
      />
    </div>
    </>
  );
};

export default GerarJogosPage;
